import React, { useState, useEffect, useCallback, useRef } from 'react';

interface PriceRangeFilterProps {
  min?: number;
  max?: number;
  rangeMin: number;
  rangeMax: number;
  onChange: (min?: number, max?: number) => void;
}

export const PriceRangeFilter: React.FC<PriceRangeFilterProps> = ({
  min,
  max,
  rangeMin,
  rangeMax,
  onChange,
}) => {
  // Use range values or defaults
  const effectiveMin = min ?? rangeMin;
  const effectiveMax = max ?? rangeMax;

  const [localMin, setLocalMin] = useState<number>(effectiveMin);
  const [localMax, setLocalMax] = useState<number>(effectiveMax);
  const minRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);

  // Sync external changes
  useEffect(() => {
    setLocalMin(min ?? rangeMin);
  }, [min, rangeMin]);

  useEffect(() => {
    setLocalMax(max ?? rangeMax);
  }, [max, rangeMax]);

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Math.min(Number(e.target.value), localMax - 1);
      setLocalMin(value);
    },
    [localMax]
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Math.max(Number(e.target.value), localMin + 1);
      setLocalMax(value);
    },
    [localMin]
  );

  const handleChangeEnd = useCallback(() => {
    // Only set values if they differ from the full range
    const newMin = localMin > rangeMin ? localMin : undefined;
    const newMax = localMax < rangeMax ? localMax : undefined;
    onChange(newMin, newMax);
  }, [localMin, localMax, rangeMin, rangeMax, onChange]);

  // Calculate percentage positions for the track highlight
  const range = rangeMax - rangeMin;
  const minPercent = ((localMin - rangeMin) / range) * 100;
  const maxPercent = ((localMax - rangeMin) / range) * 100;

  const hasActiveFilter = min !== undefined || max !== undefined;

  return (
    <div className="space-y-3">
      {/* Current values display */}
      <div className="flex items-center justify-between">
        <span
          className={[
            'text-xs font-medium px-2 py-0.5 rounded',
            hasActiveFilter ? 'bg-wine/10 text-wine' : 'bg-gray-100 text-gray-600',
          ].join(' ')}
        >
          {localMin.toFixed(0)}€
        </span>
        <span className="text-gray-300 text-xs">—</span>
        <span
          className={[
            'text-xs font-medium px-2 py-0.5 rounded',
            hasActiveFilter ? 'bg-wine/10 text-wine' : 'bg-gray-100 text-gray-600',
          ].join(' ')}
        >
          {localMax.toFixed(0)}€
        </span>
      </div>

      {/* Slider container */}
      <div className="relative h-7">
        {/* Track background */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-2 bg-gray-200 rounded-full" />

        {/* Active track highlight */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-2 bg-wine rounded-full"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />

        {/* Min slider */}
        <input
          ref={minRef}
          type="range"
          min={rangeMin}
          max={rangeMax}
          step={1}
          value={localMin}
          onChange={handleMinChange}
          onMouseUp={handleChangeEnd}
          onTouchEnd={handleChangeEnd}
          className="absolute w-full h-7 appearance-none bg-transparent pointer-events-none
            [&::-webkit-slider-thumb]:pointer-events-auto
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-5
            [&::-webkit-slider-thumb]:h-5
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-white
            [&::-webkit-slider-thumb]:border-[3px]
            [&::-webkit-slider-thumb]:border-wine
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:hover:shadow-lg
            [&::-webkit-slider-thumb]:hover:scale-110
            [&::-webkit-slider-thumb]:transition-all
            [&::-moz-range-thumb]:pointer-events-auto
            [&::-moz-range-thumb]:appearance-none
            [&::-moz-range-thumb]:w-5
            [&::-moz-range-thumb]:h-5
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-white
            [&::-moz-range-thumb]:border-[3px]
            [&::-moz-range-thumb]:border-wine
            [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:shadow-md"
          style={{ zIndex: localMin > rangeMax - 10 ? 5 : 3 }}
          aria-label="Minimum price"
        />

        {/* Max slider */}
        <input
          ref={maxRef}
          type="range"
          min={rangeMin}
          max={rangeMax}
          step={1}
          value={localMax}
          onChange={handleMaxChange}
          onMouseUp={handleChangeEnd}
          onTouchEnd={handleChangeEnd}
          className="absolute w-full h-7 appearance-none bg-transparent pointer-events-none
            [&::-webkit-slider-thumb]:pointer-events-auto
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-5
            [&::-webkit-slider-thumb]:h-5
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-white
            [&::-webkit-slider-thumb]:border-[3px]
            [&::-webkit-slider-thumb]:border-wine
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:hover:shadow-lg
            [&::-webkit-slider-thumb]:hover:scale-110
            [&::-webkit-slider-thumb]:transition-all
            [&::-moz-range-thumb]:pointer-events-auto
            [&::-moz-range-thumb]:appearance-none
            [&::-moz-range-thumb]:w-5
            [&::-moz-range-thumb]:h-5
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-white
            [&::-moz-range-thumb]:border-[3px]
            [&::-moz-range-thumb]:border-wine
            [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:shadow-md"
          style={{ zIndex: 4 }}
          aria-label="Maximum price"
        />
      </div>

      {/* Range labels */}
      <div className="flex justify-between text-[10px] text-gray-400">
        <span>{rangeMin.toFixed(0)}€</span>
        <span>{rangeMax.toFixed(0)}€</span>
      </div>
    </div>
  );
};
